import { RefObject, useEffect } from 'react';
import { useIsMobile } from './useIsMobile';

/**
 * Opt-in phone behavior for a popover whose content holds a search input, for a field that
 * sits high on its page (address search). On phones the keyboard leaves little room below
 * the trigger, so Radix flips the popover above it: the input ends up off screen and the
 * popover jumps as the result list changes height. When enabled, phones always open it
 * below, and opening scrolls the trigger to the top of the screen so the list gets the room
 * under it. Not a default: a field low on a short page cannot scroll up, and the keyboard
 * then covers the list (tire hotel picker, phone country picker). Desktop is unchanged.
 */
export const useSearchPopover = (triggerRef: RefObject<HTMLElement | null>, open: boolean, enabled: boolean) => {
  const isPhoneSearch = useIsMobile() && enabled;

  useEffect(() => {
    if (open && isPhoneSearch) {
      triggerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [open, isPhoneSearch, triggerRef]);

  if (!isPhoneSearch) {
    return { contentProps: {}, contentClassName: undefined, triggerClassName: undefined };
  }

  return {
    contentProps: { side: 'bottom', avoidCollisions: false } as const,
    // Caps the popover to the room Radix measured, so the list scrolls inside it instead of
    // running off the screen when the trigger cannot scroll all the way up.
    contentClassName: 'flex flex-col max-h-(--radix-popover-content-available-height)',
    triggerClassName: 'scroll-mt-4'
  };
};
